import { useState, useEffect, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { Logo } from '@/components/ui/Logo'
import { FormField } from '@/components/ui/FormField'
import { LoadingScreen } from '@/components/ui/LoadingScreen'
import { readPendingInviteToken } from '@/lib/pendingInvite'
import { readPendingInstructorInviteToken } from '@/lib/pendingInstructorInvite'
import { shouldRouteStudentToSelectRole } from '@/lib/studentLoginRouting'
import type { UserPrimaryRole } from '@/types'

const ROLE_LABELS = {
  school:     'Driving school',
  instructor: 'Instructor',
  student:    'Student',
} as const

const ROLE_HOME: Record<UserPrimaryRole, string> = {
  school_admin:     '/app/school',
  instructor:       '/app/instructor',
  student:          '/app/student',
  drivetrack_admin: '/app/admin',
}

function mapAuthError(message: string): string {
  if (message.includes('Invalid login credentials')) return 'Email or password is incorrect.'
  if (message.includes('Email not confirmed'))       return 'Please verify your email before logging in.'
  if (message.includes('Too many requests'))         return 'Too many attempts. Please wait a moment.'
  return 'Sign-in failed. Please try again.'
}

export default function Login() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { profile, refreshProfile } = useAuth()

  const [email,    setEmail]    = useState('')
  const [password, setPassword] = useState('')
  const [loading,  setLoading]  = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [error,    setError]    = useState('')

  // If already logged in, send to correct portal immediately.
  // useEffect avoids calling navigate() during render (React Router warning).
  useEffect(() => {
    if (profile?.primary_role) {
      navigate(ROLE_HOME[profile.primary_role], { replace: true })
    }
  }, [profile, navigate])

  if (profile?.primary_role) return <LoadingScreen />

  // Accept-invite pages are not role-specific portal pages — they independently
  // re-validate the invitation (and the signed-in account's email/role) themselves
  // server-side, so redirecting here after login is never a privilege concern the way a
  // stale ?redirect= into another role's /app/* portal would be. Without this, someone
  // bounced to /login from an invite link, who switches accounts specifically to accept
  // it, lands back on their ordinary dashboard instead of back on the invite page.
  const SAFE_NON_PORTAL_REDIRECTS = ['/accept-instructor-invite', '/accept-admin-invite']

  const redirectAfterLogin = (role: UserPrimaryRole) => {
    const redirectParam = searchParams.get('redirect')
    // Only honour redirect if it actually belongs to the role that just signed in — a
    // ?redirect= left over from an earlier, different account's bounce-to-login (e.g.
    // someone else's session expired on an admin page, then THIS browser signs in as an
    // instructor using that same lingering URL) must never send the new session into a
    // different role's portal, even momentarily before ProtectedRoute would catch it.
    if (redirectParam && (SAFE_NON_PORTAL_REDIRECTS.includes(redirectParam) || redirectParam.startsWith(ROLE_HOME[role]))) {
      navigate(redirectParam, { replace: true })
    } else {
      navigate(ROLE_HOME[role], { replace: true })
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (loading) return
    setError('')

    if (!email.trim())    return setError('Email is required.')
    if (!password.trim()) return setError('Password is required.')

    setLoading(true)
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({ email, password })
      if (signInError) {
        setError(mapAuthError(signInError.message))
        return
      }

      // Reload profile to get primary_role
      await refreshProfile()
      const { data: { user: freshUser } } = await supabase.auth.getUser()
      if (!freshUser) { setError('Session error. Please sign in again.'); return }

      // One server-sourced status call replaces three separate per-role existence checks
      // (migration 077, check_onboarding_status()) — the same consolidation
      // docs/mobile-strategy.md calls for, so a future mobile client reads this one RPC
      // instead of reverse-engineering these same three queries itself.
      const { data: onboarding, error: onboardingError } = await supabase.rpc('check_onboarding_status')
      if (onboardingError) { setError('Could not verify your account. Please try again.'); return }
      const status = onboarding as unknown as {
        primary_role: UserPrimaryRole | null
        has_school_membership: boolean
        has_student_profile: boolean
        has_instructor_profile: boolean
        school_profile_required: boolean
      }

      if (status.primary_role) {
        const role = status.primary_role
        // school_admin: verify a school membership exists. With email confirmation
        // enabled, signUp() returns no session, so the school INSERT in Register.tsx
        // cannot run. Detect the missing school here and route through select-role
        // where create_school() SECURITY DEFINER will create it atomically.
        if (role === 'school_admin' && !status.has_school_membership) {
          navigate('/auth/select-role?role=school_admin', { replace: true })
          return
        }
        // school_admin: a school still gated by the required business-profile wizard
        // (migration 080) — ProtectedRoute.tsx enforces this too, but routing here
        // directly avoids a visible bounce through the dashboard first.
        if (role === 'school_admin' && status.school_profile_required) {
          navigate('/app/school/onboarding-wizard', { replace: true })
          return
        }
        // student: verify an accepted invitation exists. With email confirmation
        // enabled, signUp() returns no session, so accept-invite in Register.tsx
        // cannot run. Detect the missing student_profiles here and route through
        // select-role where the Edge Function runs with an authenticated session.
        //
        // A pending invite token also routes here even when student_profiles ALREADY
        // exists: a student can have more than one instructor (the schema's own
        // uq_active_relationship index is scoped to (instructor_id, student_id), not
        // student_id alone), so an existing, already-confirmed student signing in to
        // accept a SECOND instructor's invitation must still reach select-role to
        // consume it — Register.tsx's existing-account branch stores the token here
        // before sending them to this login page.
        if (role === 'student') {
          const pendingStudentInviteToken = readPendingInviteToken()
          if (shouldRouteStudentToSelectRole(status.has_student_profile, pendingStudentInviteToken)) {
            navigate('/auth/select-role?role=student', { replace: true })
            return
          }
        }
        // instructor: the instructor_profiles INSERT in Register.tsx runs as anon
        // (session is null after email-confirmation signUp) and silently fails.
        // Recover here on first login using metadata stored during registration.
        if (role === 'instructor') {
          if (!status.has_instructor_profile) {
            await supabase.from('instructor_profiles').upsert({
              user_id:        freshUser.id,
              licence_number: freshUser.user_metadata?.licence_number ?? null,
              service_area:   freshUser.user_metadata?.service_area   ?? null,
            }, { onConflict: 'user_id' })
          }
          // An instructor invitation token can only survive the register → confirm-email →
          // log-in round trip via storage, not a URL param (the confirmation email link
          // lands on /auth/callback, not back on the page that started the flow — and is
          // commonly opened in a brand new tab, which is why pendingInstructorInvite.ts uses
          // localStorage, not sessionStorage). AcceptInstructorInvite.tsx writes this key when
          // it first captures the token from the URL fragment; if present, route there instead
          // of the ordinary instructor home so the pending invitation gets consumed right
          // after sign-in.
          const pendingInviteToken = readPendingInstructorInviteToken()
          if (pendingInviteToken) {
            navigate('/accept-instructor-invite', { replace: true })
            return
          }
        }
        redirectAfterLogin(role)
      } else {
        navigate('/auth/select-role', { replace: true })
      }
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSignIn = async () => {
    if (googleLoading) return
    setError('')
    setGoogleLoading(true)
    try {
      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: { access_type: 'offline', prompt: 'consent' },
        },
      })
      if (oauthError) setError('Google sign-in failed. Please try again.')
    } finally {
      setGoogleLoading(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: '24px 16px', background: '#FAF6EF',
    }}>
      <div style={{ width: '100%', maxWidth: 440 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 22 }}>
          <Logo size={60} onClick={() => navigate('/')} />
        </div>

        <div style={{
          background: '#fff', border: '1px solid rgba(27,42,74,0.08)',
          borderRadius: 18, padding: '28px 24px',
        }}>
          <div style={{ font: '600 22px Lora,serif', color: '#1B2A4A', textAlign: 'center' }}>
            Log in to your portal
          </div>
          <div style={{ font: '400 13px Inter,sans-serif', color: 'rgba(27,42,74,0.55)', textAlign: 'center', marginTop: 5, marginBottom: 18 }}>
            Enter your email and password
          </div>

          {/* Google sign-in */}
          <button
            type='button'
            onClick={handleGoogleSignIn}
            disabled={googleLoading || loading}
            style={{
              width: '100%', padding: '11px 16px', marginBottom: 14,
              background: '#fff', border: '1px solid rgba(27,42,74,0.15)',
              borderRadius: 10, font: '500 14px Inter,sans-serif', color: '#1B2A4A',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 10, opacity: googleLoading ? 0.6 : 1,
            }}
          >
            <svg width='18' height='18' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path d='M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z' fill='#4285F4'/>
              <path d='M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z' fill='#34A853'/>
              <path d='M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z' fill='#FBBC05'/>
              <path d='M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z' fill='#EA4335'/>
            </svg>
            {googleLoading ? 'Redirecting…' : 'Continue with Google'}
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <div style={{ flex: 1, height: 1, background: 'rgba(27,42,74,0.1)' }} />
            <div style={{ font: '400 11.5px Inter,sans-serif', color: 'rgba(27,42,74,0.4)' }}>or</div>
            <div style={{ flex: 1, height: 1, background: 'rgba(27,42,74,0.1)' }} />
          </div>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <FormField
              label='Email'
              type='email'
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder='you@example.com'
              autoComplete='email'
              required
            />

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <label className='field-label' style={{ margin: 0 }}>Password</label>
                <Link to='/forgot-password' style={{ font: '500 12.5px Inter,sans-serif' }}>
                  Forgot?
                </Link>
              </div>
              <FormField
                label=''
                type='password'
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder='Your password'
                autoComplete='current-password'
                required
              />
            </div>

            {error && <div className='banner-error'>{error}</div>}

            <button type='submit' className='btn-primary' disabled={loading}>
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <div style={{ font: '400 13px Inter,sans-serif', color: 'rgba(27,42,74,0.55)', textAlign: 'center', marginTop: 14 }}>
            No account?{' '}
            <Link to='/register' style={{ fontWeight: 600, color: '#0F7C6C' }}>Register</Link>
          </div>

          {/* Role hints */}
          <div style={{
            marginTop: 14, padding: '11px 13px',
            background: 'rgba(212,151,43,0.12)', border: '1px solid rgba(212,151,43,0.3)',
            borderRadius: 9,
          }}>
            <div style={{ font: '600 11px Inter,sans-serif', color: '#1B2A4A', marginBottom: 4 }}>
              Which portal will I land on?
            </div>
            {(Object.entries(ROLE_LABELS) as [string, string][]).map(([, label]) => (
              <div key={label} style={{ font: '400 12px Inter,sans-serif', color: 'rgba(27,42,74,0.65)' }}>
                {label} → your {label.toLowerCase()} portal
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
